import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { usePathname, useRouter } from 'src/routes/hook';

/**
 * The /national tab bar and page heading.
 *
 * Route-driven rather than state-driven, following the Command Center's
 * REPORT_TABS (src/sections/manager/report-dashboard/index.js): each tab owns
 * a slug, the active one is resolved from the pathname, and switching pushes a
 * route. That keeps a hard refresh, the browser back button and a pasted link
 * all landing on the right tab, which local tab state does not.
 *
 * The title and subtitle live here because neither section renders its own
 * heading, and duplicating them per page is how they drift apart.
 */
export const NATIONAL_TABS = [
  {
    value: 'overview',
    slug: 'district-overview',
    path: paths.national.districtOverview,
    label: 'District Overview',
    title: 'District Overview',
    subtitle: 'Voter universe, partisan composition & seat standing',
  },
  {
    value: 'priority',
    slug: 'priority-districts',
    path: paths.national.priorityDistricts,
    label: 'Priority Districts',
    title: 'Priority Districts',
    // The demo's subtitle ends "& voter exports". Trimmed deliberately: export
    // is not built, and a subtitle promising a feature the page lacks is the
    // same class of error as calling a hash a projection.
    subtitle: 'Key-race pulse & partisan composition',
  },
  {
    value: 'field',
    slug: 'field-contact',
    path: paths.national.fieldContact,
    label: 'Field & Contact',
    title: 'Field & Contact',
    // The demo's subtitle is "Field operations & voter contact tracking". Trimmed to what the tab
    // actually reports: it is a year-to-date snapshot of contact volume, not an operations screen —
    // there is no shift, canvasser or scheduling data behind it.
    subtitle: 'Voter contact volume by channel & geography',
  },
];

export default function NationalTabs() {
  const router = useRouter();
  const pathname = usePathname();

  // `/national` (the index route) leaves an empty slug and falls through to the
  // first tab, which is the route it actually renders.
  const slug = pathname.replace(/^\/national\/?/, '');
  const active = NATIONAL_TABS.find((tab) => tab.slug === slug) ?? NATIONAL_TABS[0];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 25, fontWeight: 800, letterSpacing: '-.02em', color: '#1a1d24' }}>
        {active.title}
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#8a909b', mt: '4px' }}>{active.subtitle}</Typography>

      <Tabs
        value={active.value}
        onChange={(_, value) => {
          const tab = NATIONAL_TABS.find((t) => t.value === value);
          if (tab) router.push(tab.path);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mt: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {NATIONAL_TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>
    </Box>
  );
}
